import React, { useState, useRef, useEffect } from 'react';
import './SelfieCapture.css';

export interface SelfieCaptureProps {
  onCapture: (imageData: string) => void;
  onCancel: () => void;
  countdownSeconds?: number;
}

/**
 * SelfieCapture component provides a camera interface for users to take selfies
 * Uses native camera API for better mobile compatibility
 */
const SelfieCapture: React.FC<SelfieCaptureProps> = ({
  onCapture,
  onCancel,
  countdownSeconds = 3
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdown, setCountdown] = useState(countdownSeconds);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [useNativeCamera, setUseNativeCamera] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);

  // Initialize webcam
  const setupCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false
      });
      
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        setIsReady(true);
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Unable to access camera. Please ensure you have granted camera permissions.');
    }
  };

  // Initialize camera on component mount
  useEffect(() => {
    if (!useNativeCamera) {
      setupCamera();
    }

    // Cleanup function to stop camera when component unmounts
    return () => {
      if (stream) {
        const tracks = stream.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, [useNativeCamera]);

  // Handle countdown timer
  useEffect(() => {
    if (!isCountingDown || countdown <= 0) return;

    const timer = setTimeout(() => {
      if (countdown > 1) {
        setCountdown(countdown - 1);
      } else {
        setCountdown(0);
        takePicture();
        setIsCountingDown(false);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [isCountingDown, countdown]);

  // Take picture from webcam
  const takePicture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to data URL
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(imageData);
    
    // Stop the camera stream after capturing
    if (stream) {
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
      setStream(null);
      setIsReady(false);
    }
  };

  // Handle file selection from native camera
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target?.result as string;
        setCapturedImage(imageData);
      };
      reader.readAsDataURL(file);
    }
  };

  // Start countdown to take picture
  const startCountdown = () => {
    setIsCountingDown(true);
    setCountdown(countdownSeconds);
  };

  // Reset and allow retaking picture
  const retakePicture = () => {
    setCapturedImage(null);
    setError(null);
    
    if (useNativeCamera) {
      // Reset file input for native camera
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      // Restart webcam for WebRTC camera
      setupCamera();
    }
  };

  // Toggle between native camera and WebRTC
  const toggleCameraMode = () => {
    // Cleanup current camera
    if (stream) {
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
      setStream(null);
    }
    
    setUseNativeCamera(!useNativeCamera);
    setCapturedImage(null);
    setError(null);
    setIsReady(false);
  };

  // Confirm and send captured image to parent
  const confirmPicture = () => {
    if (capturedImage) {
      onCapture(capturedImage);
    }
  };

  // Trigger native camera
  const triggerNativeCamera = () => {
    fileInputRef.current?.click();
  };

  // Render error state
  if (error) {
    return (
      <div className="selfie-capture error">
        <div className="error-message">{error}</div>
        <div className="error-controls">
          <button className="retry-button" onClick={() => {
            setError(null);
            if (!useNativeCamera) {
              setupCamera();
            }
          }}>
            Retry
          </button>
          <button className="toggle-camera-button" onClick={toggleCameraMode}>
            {useNativeCamera ? 'Use Webcam' : 'Use Camera App'}
          </button>
          <button className="cancel-button" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="selfie-capture">
      <div className="selfie-container">
        {!capturedImage ? (
          // Camera view
          <>
            {!useNativeCamera ? (
              // WebRTC webcam view
              <>
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline 
                  muted
                  className="camera-preview"
                />
                
                {isCountingDown && (
                  <div className="countdown-overlay">
                    <div className="countdown-number">{countdown}</div>
                  </div>
                )}
                
                <div className="camera-controls">
                  {isReady && !isCountingDown && (
                    <button 
                      className="capture-button"
                      onClick={startCountdown}
                    >
                      Take Photo
                    </button>
                  )}
                  <button 
                    className="toggle-camera-button"
                    onClick={toggleCameraMode}
                  >
                    Use Camera App
                  </button>
                  <button 
                    className="cancel-button"
                    onClick={onCancel}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              // Native camera trigger view
              <>
                <div className="native-camera-prompt">
                  <div className="camera-icon">📷</div>
                  <h3>Take a Selfie</h3>
                  <p>Click the button below to open your camera app</p>
                </div>
                
                <div className="camera-controls">
                  <button 
                    className="capture-button"
                    onClick={triggerNativeCamera}
                  >
                    Open Camera
                  </button>
                  <button 
                    className="toggle-camera-button"
                    onClick={toggleCameraMode}
                  >
                    Use Webcam
                  </button>
                  <button 
                    className="cancel-button"
                    onClick={onCancel}
                  >
                    Cancel
                  </button>
                </div>
                
                {/* Hidden file input for native camera */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="user"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </>
            )}
          </>
        ) : (
          // Review captured image
          <>
            <div className="captured-image-container">
              <img 
                src={capturedImage} 
                alt="Captured selfie" 
                className="captured-image" 
              />
            </div>
            
            <div className="review-controls">
              <button 
                className="retake-button"
                onClick={retakePicture}
              >
                Retake
              </button>
              <button 
                className="confirm-button"
                onClick={confirmPicture}
              >
                Use Photo
              </button>
              <button 
                className="cancel-button"
                onClick={onCancel}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
      
      {/* Hidden canvas for capturing frames */}
      <canvas 
        ref={canvasRef} 
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default SelfieCapture;
